import { ReactNode } from 'react'
import { Document, Page } from 'react-pdf'
import { Attachment } from '@healthnz-prm/fhir-engine-renderer/types'
import { useObjectUrl } from '@/hooks/useObjectUrl'

// Keeps white pages and images from blending into the form background.
// w-fit/self-start stop a flex parent stretching the frame past its content.
function PreviewFrame({ children }: { children: ReactNode }) {
  return (
    <div className='border-border inline-flex w-fit max-w-full self-start overflow-hidden rounded-md border shadow-sm'>
      {children}
    </div>
  )
}

interface ImageRendererProps {
  fileName?: string
  fileUrl?: string
}

function ImageRenderer({ fileName, fileUrl }: ImageRendererProps) {
  return (
    <img
      alt={fileName ? `Selected image: ${fileName}` : 'Selected image'}
      style={{
        display: 'block',
        maxHeight: '200px',
        maxWidth: '100%'
      }}
      src={fileUrl}
    />
  )
}

interface PdfRendererProps {
  file: File
}

function PdfRenderer({ file }: PdfRendererProps) {
  return (
    <Document error='Error loading PDF' file={file} loading='Loading PDF...'>
      <Page
        // Must stay opaque: pdf.js composites soft-masked/transparency groups
        // against the canvas backdrop, and a transparent backdrop makes those
        // regions render as black boxes.
        canvasBackground='#ffffff'
        pageNumber={1}
        renderAnnotationLayer={false}
        renderTextLayer={false}
        scale={0.25}
      />
    </Document>
  )
}

export interface FilePreviewProps {
  /** Attachment already held in form state, e.g. after remounting the input. */
  attachment: Attachment | null
  /** File picked in this session. Only this can be previewed, see below. */
  file: File | null
  isLoading: boolean
}

export function FilePreview({ attachment, file, isLoading }: FilePreviewProps) {
  const fileUrl = useObjectUrl(file)

  if (isLoading)
    return <p className='text-muted-foreground text-sm'>Loading file</p>

  // Navigating away and back unmounts the input, so a previously uploaded
  // attachment survives only in form state. We only get back 'Binary/:id'.
  // TODO: A later improvement could fetch the actual file bytes for preview if needed.
  if (file === null) {
    if (attachment)
      return (
        <p className='text-muted-foreground text-xs'>
          Uploaded file:{' '}
          <span className='italic'>{attachment.title || 'attachment'}</span>
        </p>
      )

    // TODO: replace 'file' with specific file type accepted
    return <p className='text-muted-foreground text-sm'>No file selected</p>
  }

  if (file.type.startsWith('image/')) {
    return (
      <PreviewFrame>
        <ImageRenderer fileName={file.name} fileUrl={fileUrl} />
      </PreviewFrame>
    )
  }

  if (file.type === 'application/pdf') {
    return (
      <PreviewFrame>
        <PdfRenderer file={file} />
      </PreviewFrame>
    )
  }

  // TODO: replace 'file' with specific file type accepted
  return (
    <p className='text-muted-foreground text-xs'>
      Selected file: <span className='italic'>{file.name}</span>
    </p>
  )
}
